import type { TabulatorFull as Tabulator } from 'tabulator-tables';

/**
 * Отмена и повтор в таблицах ввода (Ctrl+Z / Ctrl+Y), как в Excel.
 * Перед каждым изменением (правка ячейки, вставка, вырезание, Delete, очистка таблицы) запоминается
 * снимок всей таблицы — поэтому вставка блока из Excel отменяется одним нажатием, целиком.
 */
const LIMIT = 100;

interface Stack {
  undo: string[];
  redo: string[];
}

const stacks = new WeakMap<Tabulator, Stack>();

export function enableUndo(table: Tabulator): void {
  stacks.set(table, { undo: [], redo: [] });
}

export function snapshot(table: Tabulator): string {
  return JSON.stringify(table.getData());
}

/** Запомнить готовый снимок (сделанный до изменения). */
export function pushSnapshot(table: Tabulator, snap: string): void {
  const s = stacks.get(table);
  if (!s) return;
  if (s.undo[s.undo.length - 1] === snap) return;
  s.undo.push(snap);
  if (s.undo.length > LIMIT) s.undo.shift();
  s.redo = [];
}

/** Запомнить текущее состояние перед изменением. */
export function recordUndo(table: Tabulator): void {
  pushSnapshot(table, snapshot(table));
}

async function restore(table: Tabulator, from: string[], to: string[]): Promise<boolean> {
  const prev = from.pop();
  if (prev === undefined) return false;
  to.push(snapshot(table));
  await table.replaceData(JSON.parse(prev) as never[]);
  return true;
}

export async function undo(table: Tabulator): Promise<boolean> {
  const s = stacks.get(table);
  return s ? restore(table, s.undo, s.redo) : false;
}

export async function redo(table: Tabulator): Promise<boolean> {
  const s = stacks.get(table);
  return s ? restore(table, s.redo, s.undo) : false;
}
